/**
 * Reports Page
 *
 * Financial reports and analytics.
 */

import React, { useState, useMemo } from 'react';
import type { CentsAmount } from '@business-assistant/shared';
import '../accounting.css';

// =============================================================================
// TYPES
// =============================================================================

interface ReportsPageProps {
  onNavigate?: (path: string) => void;
  onBack?: () => void;
}

type ReportType =
  | 'profit_loss'
  | 'balance_sheet'
  | 'cash_flow'
  | 'vat_summary'
  | 'aging_report'
  | 'revenue_by_customer'
  | 'expense_by_category';

interface ReportDefinition {
  id: ReportType;
  title: string;
  description: string;
  icon: string;
  category: 'financial' | 'tax' | 'analysis';
}

// =============================================================================
// CONSTANTS
// =============================================================================

const REPORTS: ReportDefinition[] = [
  {
    id: 'profit_loss',
    title: 'Conto Economico',
    description: 'Ricavi, costi e utile del periodo',
    icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
    category: 'financial',
  },
  {
    id: 'balance_sheet',
    title: 'Stato Patrimoniale',
    description: 'Attivita, passivita e patrimonio netto',
    icon: 'M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3',
    category: 'financial',
  },
  {
    id: 'cash_flow',
    title: 'Flussi di Cassa',
    description: 'Entrate e uscite di liquidita',
    icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
    category: 'financial',
  },
  {
    id: 'vat_summary',
    title: 'Riepilogo IVA',
    description: 'IVA a debito e a credito',
    icon: 'M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2zM10 8.5a.5.5 0 11-1 0 .5.5 0 011 0zm5 5a.5.5 0 11-1 0 .5.5 0 011 0z',
    category: 'tax',
  },
  {
    id: 'aging_report',
    title: 'Scadenzario Crediti',
    description: 'Analisi per anzianita dei crediti',
    icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
    category: 'analysis',
  },
  {
    id: 'revenue_by_customer',
    title: 'Fatturato per Cliente',
    description: 'Top clienti e concentrazione',
    icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
    category: 'analysis',
  },
  {
    id: 'expense_by_category',
    title: 'Costi per Categoria',
    description: 'Analisi delle spese',
    icon: 'M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z',
    category: 'analysis',
  },
];

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function formatCurrency(cents: CentsAmount): string {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
  }).format(cents / 100);
}

// =============================================================================
// COMPONENTS
// =============================================================================

function ReportCard({
  report,
  onClick,
}: {
  report: ReportDefinition;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="accounting-card--start"
    >
      <div className="accounting-icon--md accounting-flex--center">
        <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={report.icon} />
        </svg>
      </div>
      <div>
        <div>{report.title}</div>
        <div>{report.description}</div>
      </div>
    </button>
  );
}

function ProfitLossReport() {
  const data = {
    revenue: [
      { category: 'Servizi professionali', amount: 2500000 },
      { category: 'Consulenze', amount: 1500000 },
      { category: 'Vendita prodotti', amount: 560000 },
    ],
    expenses: [
      { category: 'Costi personale', amount: 1200000 },
      { category: 'Affitti e utenze', amount: 360000 },
      { category: 'Servizi esterni', amount: 280000 },
      { category: 'Marketing', amount: 150000 },
      { category: 'Spese IT', amount: 120000 },
    ],
  };

  const totalRevenue = data.revenue.reduce((sum, r) => sum + r.amount, 0);
  const totalExpenses = data.expenses.reduce((sum, e) => sum + e.amount, 0);
  const netIncome = totalRevenue - totalExpenses;

  return (
    <div className="accounting-report">
      <h3>Conto Economico - Gennaio 2024</h3>

      {/* Revenue Section */}
      <div className="accounting-report__section">
        <div className="accounting-report__row">
          <span>RICAVI</span>
          <span>{formatCurrency(totalRevenue)}</span>
        </div>
        <div>
          {data.revenue.map((item, i) => (
            <div key={i} className="accounting-report__row">
              <span>{item.category}</span>
              <span>{formatCurrency(item.amount)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Expenses Section */}
      <div className="accounting-report__section">
        <div className="accounting-report__row">
          <span>COSTI</span>
          <span>{formatCurrency(totalExpenses)}</span>
        </div>
        <div>
          {data.expenses.map((item, i) => (
            <div key={i} className="accounting-report__row">
              <span>{item.category}</span>
              <span>{formatCurrency(item.amount)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Net Income */}
      <div className="accounting-report__row">
        <span>UTILE/PERDITA</span>
        <span>{formatCurrency(netIncome)}</span>
      </div>
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function ReportsPage({ onNavigate, onBack }: ReportsPageProps) {
  const [selectedReport, setSelectedReport] = useState<ReportType | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  const filteredReports = useMemo(() => {
    if (categoryFilter === 'all') return REPORTS;
    return REPORTS.filter((r) => r.category === categoryFilter);
  }, [categoryFilter]);

  const handleBack = () => {
    if (selectedReport) {
      setSelectedReport(null);
    } else if (onBack) {
      onBack();
    }
  };

  return (
    <div className="accounting-page accounting-page--narrow">
      {selectedReport ? (
        /* Report Detail */
        <div>
          {selectedReport === 'profit_loss' && <ProfitLossReport />}
          {selectedReport !== 'profit_loss' && (
            <div className="accounting-empty">
              <svg
                width="48"
                height="48"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d={REPORTS.find((r) => r.id === selectedReport)?.icon || ''}
                />
              </svg>
              <h3>
                {REPORTS.find((r) => r.id === selectedReport)?.title}
              </h3>
              <p>Report in fase di sviluppo</p>
            </div>
          )}

          {/* Export Actions */}
          <div className="accounting-form__row">
            <button className="accounting-flex--row accounting-flex--gap-sm">
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                />
              </svg>
              Stampa
            </button>
            <button className="accounting-flex--row accounting-flex--gap-sm">
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              Esporta Excel
            </button>
          </div>
        </div>
      ) : (
        /* Report List */
        <div>
          {/* Category Filter */}
          <div className="accounting-filters">
            {[
              { id: 'all', label: 'Tutti' },
              { id: 'financial', label: 'Finanziari' },
              { id: 'tax', label: 'Fiscali' },
              { id: 'analysis', label: 'Analisi' },
            ].map((cat) => (
              <button
                key={cat.id}
                onClick={() => setCategoryFilter(cat.id)}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Report Grid */}
          <div className="accounting-grid--reports">
            {filteredReports.map((report) => (
              <ReportCard key={report.id} report={report} onClick={() => setSelectedReport(report.id)} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export type { ReportsPageProps };
